import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, LogOut } from 'lucide-react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

import NewsletterListItem from './components/NewsletterListItem';
import NewsletterEditorForm from './components/NewsletterEditorForm';
import NotificationToast from '../admin-dashboard/components/NotificationToast';
import newsletterService from '../../services/newsletterService';
import { useAuth } from '../../contexts/AuthContext';

const NewsletterAdminEditor = () => {
  const navigate = useNavigate();
  const { id: newsletterIdFromUrl } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ 
    isVisible: false, 
    message: '', 
    type: 'info' 
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Debug session on mount
  useEffect(() => {
    const debugSession = async () => {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      console.log('[Edit Page] Session check:', { 
        session: session ? 'EXISTS' : 'NULL', 
        user: session?.user?.email,
        authContextUser: user?.email,
        authLoading,
        error 
      });
    };
    debugSession();
  }, [user, authLoading]);

  // Check authentication - use shared auth state from AuthContext
  useEffect(() => {
    console.log('[Edit Page] Auth state:', { authLoading, hasUser: !!user, userEmail: user?.email });
    
    // Wait for auth to finish loading
    if (authLoading) {
      console.log('[Edit Page] Auth still loading, waiting...');
      return;
    }

    // Only redirect if definitively not authenticated
    if (!user) {
      console.log('[Edit Page] No user found after auth loaded, redirecting to login');
      const currentPath = window.location?.pathname;
      navigate(`/admin-login?redirectTo=${encodeURIComponent(currentPath)}`, { replace: true });
      return;
    }

    // User is authenticated, load newsletters
    console.log('[Edit Page] User authenticated via AuthContext:', user?.email);
    loadNewsletters();
  }, [user, authLoading, navigate]);

  // Load specific newsletter if ID is in URL
  useEffect(() => {
    console.log('[useEffect] Newsletter ID from URL:', newsletterIdFromUrl);
    console.log('[useEffect] Newsletters loaded:', newsletters?.length);
    
    if (newsletterIdFromUrl && newsletters?.length > 0) {
      const newsletter = newsletters?.find(n => n?.id === newsletterIdFromUrl);
      console.log('[useEffect] Found newsletter:', newsletter ? newsletter?.id : 'NOT FOUND');
      
      if (newsletter) {
        console.log('[useEffect] Setting selected newsletter:', {
          id: newsletter?.id,
          title: newsletter?.title
        });
        setSelectedNewsletter(newsletter);
      } else {
        console.error('[useEffect] Newsletter not found for ID:', newsletterIdFromUrl);
        showNotification('Newsletter not found', 'error');
      }
    }
  }, [newsletterIdFromUrl, newsletters]);

  const handleLogout = async () => {
    try {
      await supabase?.auth?.signOut();
      navigate('/admin-login');
    } catch (err) {
      console.error('Error logging out:', err);
      showNotification('Failed to log out', 'error');
    }
  };

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await newsletterService?.getAll();
      setNewsletters(data);
    } catch (err) {
      setError(err?.message || 'Failed to load newsletters');
      showNotification('Failed to load newsletters', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ isVisible: true, message, type });
  };

  const handleNewsletterClick = (newsletter) => {
    setSelectedNewsletter(newsletter);
  };

  const handleNewNewsletter = () => {
    setSelectedNewsletter(null);
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      setError('');

      // Check slug uniqueness
      const isUnique = await newsletterService?.isSlugUnique(
        formData?.slug, 
        selectedNewsletter?.id
      );

      if (!isUnique) {
        showNotification('This slug is already used by another newsletter', 'error');
        setSaving(false);
        return;
      }

      if (selectedNewsletter) {
        // Update existing newsletter
        await newsletterService?.update(selectedNewsletter?.id, formData);
        showNotification('Newsletter updated successfully!', 'success');
      } else {
        // Create new newsletter
        await newsletterService?.create(formData);
        showNotification('Newsletter created successfully!', 'success');
        setSelectedNewsletter(null);
      }

      // Reload newsletters list
      await loadNewsletters();
    } catch (err) {
      setError(err?.message || 'Failed to save newsletter');
      showNotification(err?.message || 'Failed to save newsletter', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedNewsletter(null);
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, don't render the page (redirect will happen in useEffect)
  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />
      <main className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Newsletter Admin Editor
              </h1>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin-dashboard')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Log Out
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              Create and edit newsletter editions with full content management
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Newsletter List */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h2 className="text-lg font-heading font-semibold text-foreground">
                    All Newsletters
                  </h2>
                  <Button
                    size="sm"
                    onClick={handleNewNewsletter}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>

                <div className="p-4 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : newsletters?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No newsletters yet</p>
                      <p className="text-xs mt-1">Create your first newsletter</p>
                    </div>
                  ) : (
                    newsletters?.map((newsletter) => (
                      <NewsletterListItem
                        key={newsletter?.id}
                        newsletter={newsletter}
                        isActive={selectedNewsletter?.id === newsletter?.id}
                        onClick={() => handleNewsletterClick(newsletter)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Editor Form */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg shadow-elevation-sm p-6">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                  {selectedNewsletter ? 'Edit Newsletter' : 'Create New Newsletter'}
                </h2>

                <NewsletterEditorForm
                  newsletter={selectedNewsletter}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isSaving={saving}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Notification Toast */}
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        isVisible={notification?.isVisible}
        onClose={() => setNotification({ ...notification, isVisible: false })}
      />
    </div>
  );
};

export default NewsletterAdminEditor;